import * as React from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { ArcadeIcon } from '@arcade/web/icons/react'

import { useDebounce } from '../../hooks/useDebounce'
import { truncate } from '../../styles'
import { spacing } from '../../theme'
import LoadingIcon from '../LoadingIcon/LoadingIcon'
import { focusRingStyles, getVariantStyles } from './arcade-button-styles'

export type ButtonSize = 'default' | 'cta' | 'compact'
export type ButtonProminence = 'prominent' | 'standard' | 'subtle'

export type ButtonProps = {
  isDestructive?: boolean
  prominence?: ButtonProminence
  size?: ButtonSize
  Icon?: ArcadeIcon
  disabled?: boolean
  isLoading?: boolean
  loadingDelay?: number
  linesToTruncate?: number
  ariaLabel?: string
  as?: React.ElementType
} & React.ButtonHTMLAttributes<HTMLButtonElement>

type StyleProps = Pick<ButtonProps, 'prominence' | 'size' | 'linesToTruncate' | 'isDestructive'> & {
  isFunctionallyDisabled?: boolean
  isFunctionallyLoading?: boolean
  isVisuallyDisabled?: boolean
  isVisuallyLoading?: boolean
}

const ButtonContainer = styled.button<StyleProps>`
  position: relative;
  display: grid;
  background: transparent;
  border: 0;
  width: 100%;
  padding: ${({ theme: { spacing } }) => spacing.empty};
  outline: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  ${({ size }) => {
    switch (size) {
      case 'default':
      case 'compact':
        return css`
          width: auto;
        `
      default:
        return
    }
  }}

  ${({ isVisuallyLoading }) =>
    isVisuallyLoading &&
    css`
      cursor: default;
    `}

    ${({ isVisuallyDisabled }) =>
    isVisuallyDisabled &&
    css`
      cursor: not-allowed;
    `}

    /* focus ring styles */
    ${({ theme }) => focusRingStyles({ theme })}
`

export const StyledButton = styled.span<StyleProps>`
  /* text */
  font-family: inherit;
  text-align: center;
  letter-spacing: 0.01em;

  /* layout */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => `${props.theme.spacing.empty} ${props.theme.spacing.m}`};
  height: 3rem;
  max-height: 3rem;
  box-sizing: border-box;

  /* typography */
  ${props => props.theme.typography.buttonText}

  /* motion */
  ${props => props.theme.motion.standardTransition}
  transition-property: transform, background, color, opacity;

  border-radius: 1.5rem;
  cursor: pointer;

  ${({ linesToTruncate }) =>
    linesToTruncate &&
    linesToTruncate > 1 &&
    css`
      ${truncate(linesToTruncate)}
      height: auto;
      max-height: initial;
      border-radius: 2rem;
      padding: ${spacing.xxs} ${spacing.default};
    `}

  /* apply variant styles */
  ${({ isFunctionallyDisabled, isVisuallyDisabled, theme, prominence, size, isDestructive }) =>
    getVariantStyles({ isFunctionallyDisabled, isVisuallyDisabled, theme, prominence, size, isDestructive })}
`

const LoadingPosition = styled.span`
  position: absolute;
  display: flex;
`

const StyledChildren = styled.span<{ hasIcon: boolean; isLoading: boolean }>`
  ${({ hasIcon }) => hasIcon && `padding-left: ${spacing.xs};`}
  ${({ isLoading }) => isLoading && 'visibility: hidden;'}
  display: flex;
`

const IconContainer = styled.span<{ isLoading: boolean }>`
  ${({ isLoading }) => isLoading && 'visibility: hidden;'}
  display: flex;
`

// Keep button Buttoncontainer to avoid unclickable edges when using this component as an `a` tag caused by `scale` property
const ArcadeButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      ariaLabel,
      children,
      prominence,
      disabled,
      isLoading,
      loadingDelay = 0,
      size = 'default',
      linesToTruncate,
      Icon,
      isDestructive,
      ...rest
    },
    ref
  ) => {
    const isFunctionallyLoading = isLoading
    const isVisuallyLoading = useDebounce(isLoading, isLoading ? loadingDelay : 0)

    const isFunctionallyDisabled = disabled || isLoading
    const isVisuallyDisabled = disabled

    // According to figma, compact buttons should have a smaller icon (16) while default and cta should have a bigger icon (24)
    const iconSize = size === 'compact' ? 16 : 24

    const styleProps: StyleProps = {
      isDestructive,
      isFunctionallyDisabled,
      isFunctionallyLoading,
      isVisuallyDisabled,
      isVisuallyLoading,
      linesToTruncate,
      prominence,
      size,
    }

    return (
      <ButtonContainer
        ref={ref}
        {...styleProps}
        disabled={isFunctionallyDisabled}
        aria-label={ariaLabel || 'button'}
        {...rest}
      >
        <StyledButton {...styleProps}>
          {isVisuallyLoading && (
            <LoadingPosition>
              <LoadingIcon size="1.5rem" color="current" />
            </LoadingPosition>
          )}
          <IconContainer isLoading={!!isVisuallyLoading}>
            {Icon && <Icon data-testid="arcade-button-icon" size={iconSize} />}
          </IconContainer>
          <StyledChildren isLoading={!!isVisuallyLoading} hasIcon={!!Icon}>
            {children}
          </StyledChildren>
        </StyledButton>
      </ButtonContainer>
    )
  }
)

export default ArcadeButton
